import { eq } from "drizzle-orm";
import { ingredient, type PowerSyncDrizzleDB, recipe_item } from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`: a
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). Mutations **can throw**: the caller
 * caller must propagate it, never swallow it.
 */

/**
 * Creates an ingredient with initial stock (integer). Stock is DERIVED BALANCE
 * that the server deducts when charging orders; client reads it, does not recalculate it
 * per sale (prevents double discount across terminals).
 */
export async function createIngredientMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { unit?: string; stock?: number; cost?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(ingredient).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    unit: opts?.unit ?? "unit",
    stock: Math.trunc(opts?.stock ?? 0),
    cost: Math.max(0, Math.trunc(opts?.cost ?? 0)),
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Adjusts cost (cents per unit of stock) of an ingredient.*/
export async function setIngredientCostMutation(
  db: PowerSyncDrizzleDB,
  ingredientId: string,
  cost: number,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    ingredient,
    ingredient.id,
    ingredientId,
    { cost: Math.max(0, Math.trunc(cost)) },
    options,
  );
}

/** Manual adjustment of ingredient stock (physical count, shrinkage, replenishment).*/
export async function setIngredientStockMutation(
  db: PowerSyncDrizzleDB,
  ingredientId: string,
  stock: number,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    ingredient,
    ingredient.id,
    ingredientId,
    { stock: Math.trunc(stock) },
    options,
  );
}

export async function deleteIngredientMutation(
  db: PowerSyncDrizzleDB,
  ingredientId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    ingredient,
    ingredient.id,
    ingredientId,
    { deleted_at: nowISO() },
    options,
  );
}

/**
 * Adds (or updates) a recipe line: product `productId` consumes
 * `qty` units of ingredient `ingredientId` for each unit sold.
 */
export async function setRecipeItemMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  productId: string,
  ingredientId: string,
  qty: number,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(recipe_item).values({
    id,
    organization_id: organizationId,
    product_id: productId,
    ingredient_id: ingredientId,
    qty: Math.max(0, Math.trunc(qty)),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteRecipeItemMutation(
  db: PowerSyncDrizzleDB,
  recipeItemId: string,
): Promise<void> {
  await db.delete(recipe_item).where(eq(recipe_item.id, recipeItemId));
}
